<template>
  <!--
    쿠폰 메시지 관리 > 등록 화면 
    퍼블: PubUI_FOMB10309PC
  -->
  <div class="content-box">
    <!-- 사용자 정보 영역 -->
    <HconMyPageUsrInfo />

    <div class="sub-wrapper">
      <!-- 마이페이지 왼쪽 사이드 메뉴 영역 -->
      <HconMyPageSideMenu />

      <div class="sub-contents">
        <div class="border-title title">
          <h4 class="title">쿠폰 메시지 등록<span class="desc">메시지를 직접 입력하거나 추천 문구(화면 우측)를 선택해 등록하세요.</span></h4>
        </div>
        <div class="flex gap20">
          <div class="msg-box">
            <h4 class="mb16 fs15 font-midum">메시지 입력</h4>
            <div class="form-box hg382">
              <div class="form-box">
                <div class="form-group">
                  <HconInput 
                    type="message" 
                    v-model="inputCouponMsg" 
                    label="쿠폰 메시지 입력" 
                    :hide-label="true" 
                    inputClass="md mb4" 
                    :max="20" 
                    placeholder="제목을 입력해주세요."
                  />
                </div>
              </div>
              <div class="form-box">
                <div class="form-group">
                  <HconTextarea 
                    v-model="couponMsgTextarea" 
                    :bottomContent="true" 
                    textAreaBoxClass="hg247" 
                    charsBoxClass="top249" 
                    :showRgtButton="false" 
                    :maxChars="200" 
                    placeholder="내용을 입력해주세요."
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="parse-box">
            <h4 class="mb16 fs15 font-midum">추천 문구</h4>
            <div class="box-border rounded8 list-box p0">
              <ul class="recomd-pharse-list">
                <li class="recomd-pharse-item" v-for="(item, index) in recomdPharseList" :key="index">
                  <button type="button" class="link-text" @click="handlePharseSelected(item.cpnMsgTtl, item.cpnMsgCntn)">
                    <img :src="item.src" width="24" height="24" alt="">
                    <span class="text-box">
                      <span class="title">{{ item.cpnMsgTtl }}</span>
                      <span class="contents">{{ item.cpnMsgCntn }}</span>
                    </span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="box-gray p40 mt40">
          <h5 class="title">특수문자 입력 안내</h5>
          <ul class="dot-list mt24">
            <li>메시지 내용에는 <span class="text-green">특수문자 패드에서 제공하는 특수문자 내에서 선택하여 사용</span>할 수 있습니다.</li>
            <li>지정된 특수문자 외 특수문자 입력 시 문자 깨짐 현상이 발생합니다.</li>
          </ul>
        </div>
        <div class="button-box col2 mt40">
          <button type="button" class="btn btn-outline lg" @click="handleConfirmCancelAlert">취소</button>
          <button type="button" class="btn lg" @click="handleConfirmAlert">등록</button>
        </div>
      </div>
    </div> 
  </div>
  <HconLoading v-model="isLoadingShow" />
</template>
<script lang="ts" setup>
import { ref } from 'vue';
import HconMyPageUsrInfo from '@/components/corner/pc/HconCornerUserInfoPC.vue';
import HconMyPageSideMenu from '@/components/corner/pc/HconCornerMypageSideMenuPC.vue';
import HconInput from '@/components/com/HconComPCInputField.vue';
import HconTextarea from '@/components/com/HconComPCTextarea.vue';
import type { MsgDto, MsgSaveDto } from '~/types/mypage/backend-dto-types';
import { MEMBER_CODE } from '@/constants/hconMessageConstants';
import HconLoading from '@/components/com/HconComPCLoading.vue';

const fetchBuilder  = useHconFetchBuilder();
// 라우터
const router = useRouter();
const isLoadingShow = ref(false)

// 팝업
const {
  showAlert,
  showConfirm
} = usePopup();

// 메시지 초기값
const inputCouponMsg = ref('');
const couponMsgTextarea = ref('');

// 추천 문구 타입 정의
interface RecomdPharseData {
  src: string
  cpnMsgTtl: string
  cpnMsgCntn: string
}

// 추천 문구 이미지
import msgImg01 from '@/assets/images/pc/sub-hcon/message-1.png';
import msgImg02 from '@/assets/images/pc/sub-hcon/message-2.png';
import msgImg03 from '@/assets/images/pc/sub-hcon/message-3.png';
import msgImg04 from '@/assets/images/pc/sub-hcon/message-4.png';

// 관리자가 등록한 쿠폰 메시지 조회 API 결과 값
const mgrMsgList = ref<MsgDto[]>([]);
// 추천 문구에 조회되는 데이터 (이미지 추가한 리스트)
const recomdPharseList = ref<RecomdPharseData[]>([]);
// 신규 등록하는 쿠폰 메시지
const newMsgData = ref<MsgSaveDto>();
// 생일인 경우만 msgImg01로 적용 그 외는 랜덤 이미지로 반영 
const randomImages = [msgImg02, msgImg03, msgImg04];
const determineImage = (title: string) => {
  if (title.includes('생일')) {
    return msgImg01;
  }

  const randomIndex = Math.floor(Math.random() * randomImages.length);
  return randomImages[randomIndex];
}

// 추천 문구 선택 시 제목과 내용 반영
const handlePharseSelected = (title: string, contents: string) => {
  inputCouponMsg.value = title;
  couponMsgTextarea.value = contents;
}

// 관리자가 등록한 쿠폰 메시지 조회
const getMgrMsgList = () => {
  const mgrMsgListFetcher =
    fetchBuilder.get<AjaxResult<MsgDto[]>>('/api/v1/mypage/coupon-message/mgr-list')
    .progressMessage("잠시만 기다려주세요...")
    .build()
    .execute()
    .then((result) => {
      if(result.resultCode != '200') {
        showAlert(result.resultMessage);
      } else {
        mgrMsgList.value = result.data;

        // 추천 문구 데이터 title에 따른 src 랜덤 이미지 추가
        recomdPharseList.value = mgrMsgList.value.map(item => ({
          ...item,
          src: determineImage(item.cpnMsgTtl)
        })) as RecomdPharseData[];
      }
  });
}
interface FetchError {
    status: number;
    resultCode: number;
    resultMessage: string;
}
// [등록] 버튼 선택시 알럿 팝업
const handleConfirmAlert = async () => {
    if(isLoadingShow.value) return;
    // 필수값 입력 체크 : 쿠폰 메시지 제목 및 내용
    if (!inputCouponMsg.value.trim()) {
    showConfirm({
      title: "메시지 제목 입력",
      message: MEMBER_CODE.ALT_HCON_9117, // 'MY 메시지 제목을 입력해주세요.'
      confirmText: '확인',
      showCancelButton: false,
    })
  
    return;
  }
  if (!couponMsgTextarea.value.trim()) {
    showConfirm({
      title: "메시지 내용 입력",
      message: MEMBER_CODE.ALT_HCON_9118, // 'MY 메시지 내용을 입력해주세요.'
      confirmText: '확인',
      showCancelButton: false,
    })

    return;
  }

  const chkSpecialChar = HconCommonUtils.chkTextAreaSpecialChar(couponMsgTextarea.value);
  if (!chkSpecialChar) {
    showAlert('허용되지 않은 특수문자가 포함되어 있습니다.');
  
    return;
  } else {
      newMsgData.value = {
      cpnMsgTtl: inputCouponMsg.value,
      cpnMsgCntn: couponMsgTextarea.value
    }
    
    try {
      isLoadingShow.value = true;
    await fetchBuilder.post<AjaxResult<MsgSaveDto>>('/api/v1/mypage/coupon-message/save')
      .body(newMsgData.value)
      .progressMessage("잠시만 기다려주세요...")
      .build()
      .execute()
      .then(async (result) => {
        if(result.resultCode != '200') {
          showAlert(result.resultMessage);
        } else {
          const res = await showConfirm({
            title: "메시지 등록 완료",
            message: MEMBER_CODE.ALT_HCON_9096,//  `MY 메시지가 저장되었습니다.<br />문자 메시지는 <span class="text-green">최대 20개까지 등록</span>할 수 있습니다.`,
            confirmText: '확인',
            showCancelButton: false,
          });

          if (res) {
            // 목록 페이지 이동
            router.push('/pc/mypage/coupon-message');
          }
        }
      })
    } catch (err ) {
      const error = err as FetchError;

      if(error.resultCode == 29185) {
        showAlert({
        message: MEMBER_CODE.ALT_HCON_9185
        })
      } else {
        showAlert({
        message: error.resultMessage
        })
      }
      // showAlert({
      // message : err,
      // onConfirm: () => {  
      // }})
    } finally {
      // 작업중 해제
      isLoadingShow.value = false;
    }
  }

  
}

// [취소] 버튼 선택 시 알럿 팝업
const handleConfirmCancelAlert = async () => {
  const result = await showConfirm({
    title: '메시지 등록 취소',
    message: MEMBER_CODE.ALT_HCON_9097,// `작성 중인 내용은 저장되지 않습니다.<br />목록으로 이동하시겠습니까?`,
    confirmText: '확인',
    cancelText: '취소'
  })

  if (result) {
    // 목록 페이지 이동
    router.push('/pc/mypage/coupon-message');
  }
}

onMounted(() => {
  getMgrMsgList();
});

</script>
<style lang="scss" scoped>
@use "@/assets/scss/pc/order-hcon.scss" as *;
@use "@/assets/scss/pc/mypage-hcon.scss" as *;

</style>